import { useState, useSyncExternalStore, type ReactNode } from 'react';
import { BellRing, Download, Share, Smartphone, SquarePlus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { usePushNotifications } from '@/hooks/use-push';
import { installPromptStore } from '@/lib/pwa';

const DISMISSED_KEY = 'oro.pushPrompt.dismissedAt';
const DISMISS_FOR_MS = 7 * 24 * 60 * 60 * 1000;

/** Storage can throw (private mode, blocked site data); treat that as "not dismissed". */
function readDismissed(): boolean {
  try {
    const at = Number(localStorage.getItem(DISMISSED_KEY));
    return at > 0 && Date.now() - at < DISMISS_FOR_MS;
  } catch {
    return false;
  }
}

function writeDismissed(): void {
  try {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
  } catch {
    // Nothing to do; the card simply comes back next visit.
  }
}

/**
 * One compact card nudging the user toward notifications, in whichever form
 * this device needs: add to Home Screen on iOS, install on Chromium, or plain
 * permission. Renders nothing once permission is decided or push cannot work.
 */
export function PushPrompt() {
  const [dismissed, setDismissed] = useState(readDismissed);
  const canInstall = useSyncExternalStore(
    installPromptStore.subscribe,
    installPromptStore.getSnapshot,
  );
  const push = usePushNotifications();

  if (dismissed) return null;

  let body: ReactNode = null;
  if (push.needsInstallOnIos) {
    body = (
      <PromptRow
        icon={<Smartphone className="h-4 w-4" />}
        title="Add to Home Screen to get notifications"
        description={
          <>
            Tap <Step icon={<Share className="h-3 w-3" />}>Share</Step>, then{' '}
            <Step icon={<SquarePlus className="h-3 w-3" />}>Add to Home Screen</Step>, and open the
            portal from there.
          </>
        }
      />
    );
  } else if (canInstall) {
    body = (
      <PromptRow
        icon={<Download className="h-4 w-4" />}
        title="Install the app"
        description="Open the portal from your home screen or dock, with an unread badge on its icon."
        action={
          <Button size="sm" onClick={() => void installPromptStore.promptInstall()}>
            Install app
          </Button>
        }
      />
    );
  } else if (push.supported && push.permission === 'default') {
    body = (
      <PromptRow
        icon={<BellRing className="h-4 w-4" />}
        title="Turn on notifications"
        description="Hear about new leads, approvals and messages, even with the portal closed."
        action={
          <Button
            size="sm"
            disabled={push.busy}
            onClick={() => void push.enable().catch(() => undefined)}
          >
            {push.busy ? 'Turning on…' : 'Turn on notifications'}
          </Button>
        }
      />
    );
  }

  if (!body) return null;

  return (
    <Card className="relative animate-fade-in p-3 pr-10">
      {body}
      <button
        type="button"
        onClick={() => {
          writeDismissed();
          setDismissed(true);
        }}
        aria-label="Dismiss"
        className="absolute right-2 top-2 rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </Card>
  );
}

/** Icon tile, two lines of text, and an optional action that drops below on phones. */
function PromptRow({
  icon,
  title,
  description,
  action,
}: {
  icon: ReactNode;
  title: string;
  description: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          {icon}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-medium leading-tight">{title}</p>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{description}</p>
        </div>
      </div>
      {action && <div className="shrink-0 sm:pl-2">{action}</div>}
    </div>
  );
}

/** An inline "tap this" reference: the icon the user will see, plus its label. */
function Step({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap font-medium text-foreground">
      {icon}
      {children}
    </span>
  );
}
